import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  IconButton,
  Card,
  Chip,
} from '@mui/material';
import CascadingSidePanel from './CascadingSidePanel';
import { closeProductSidePanel } from '../../store/slices/sidePanelSlice';
import { formatStatusText } from "../../utils/formatters";

interface Product {
  id: string;
  name?: string;
  type: string;
  status?: string;
  quantity?: number;
  completed?: number;
  color?: string;
  frame?: any[];
  panel?: any[];
  panelWindow?: any[];
  net?: any[];
  openableType?: any[];
}

interface ProductSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseAll?: () => void;
  products: Product[];
  onProductSelect?: (product: Product) => void;
  title?: string;
  loading?: boolean;
  onBackToOrders?: () => void;
  selectedProductId?: string; // Track selected product
}

const ProductSidePanel: React.FC<ProductSidePanelProps> = ({
  isOpen,
  onClose,
  onCloseAll,
  products,
  onProductSelect,
  title = "Sets",
  loading = false,
  onBackToOrders,
  selectedProductId
}) => {
  const dispatch = useDispatch();
  console.log('ProductSidePanel - products:', products);

  const capitalizeWords = (str: string) => {
    return str.replace(/\b\w/g, l => l.toUpperCase());
  };


  return (
    <CascadingSidePanel
      isOpen={isOpen}
      onClose={onClose}
      onCloseAll={onCloseAll}
      panelId="product-panel"
      title={title}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
          {/* Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {onBackToOrders && (
                <IconButton 
                  onClick={onBackToOrders}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  ←
                </IconButton>
              )}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {title || 'Sets'}
              </Typography>
            </Box>
            <IconButton 
              // onClick={onCloseAll || onClose}
              onClick={() => {
                dispatch(closeProductSidePanel())
              }}
              size="small"
            >
              ✕
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '200px',
                textAlign: 'center'
              }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Loading products...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we fetch the products for this order.
                </Typography>
              </Box>
            ) : products.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '200px',
                textAlign: 'center'
              }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No products found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This order doesn't have any products yet.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {products.map((product, index) => {
                  const isSelected = selectedProductId === product.id;
                  return (
                    <Card 
                      key={product.id || index}
                      sx={{ 
                        p: 1.5, 
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.12)' : 'action.hover',
                          transform: 'translateY(-1px)',
                          boxShadow: isSelected ? 3 : 1,
                          borderColor: 'primary.main'
                        },
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: isSelected ? 2 : 0,
                      }}
                      onClick={() => onProductSelect(product)}
                    >
                      {/* Product Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: 'primary.main',
                            flexShrink: 0
                          }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {capitalizeWords(product.name) || capitalizeWords(product.type) || `Product ${index + 1}`}
                          </Typography>
                        </Box>
                        <Chip
                          label={formatStatusText(product.status || 'pending')}
                          color={product.status === 'completed' ? 'success' : 
                                 product.status === 'in_progress' ? 'primary' : 'warning'}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>

                      {/* Product Details */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Type: {capitalizeWords(product.type)}
                        </Typography>
                        {product.color && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Color: {formatStatusText(product.color)}
                          </Typography>
                        )}
                        {product.quantity && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Quantity: {product.quantity}
                          </Typography>
                        )}
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
    </CascadingSidePanel>
  );
};

export default ProductSidePanel;
